import React, { useMemo } from "react";
import { Grid, Stack, Text, TextInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import {
  CLIENT_ONLINE_APPROVAL_SIGNATURE,
  getArFormValues,
  resolvePartESignatories,
  setArFormField,
} from "../helpers/arFormUtils";
import { isWidgetReadOnly } from "../helpers/formReadOnly";
import OrderArFormBlock, { OrderArFormStack } from "./orderArFormBlock";

const fieldStyles = {
  label: {
    minHeight: 44,
    lineHeight: 1.35,
    display: "block",
    whiteSpace: "normal",
  },
};

const BilingualFieldLabel = ({ english, chinese }) => (
  <>
    {english}
    <br />
    {chinese}
  </>
);

const toDateOrNull = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const SignatureField = ({ label, value }) => (
  <TextInput
    styles={fieldStyles}
    label={label}
    value={value}
    readOnly
  />
);

const SignDateField = ({ label, value, onChange, locked }) => (
  <DatePicker
    styles={fieldStyles}
    label={label}
    value={toDateOrNull(value)}
    onChange={onChange}
    inputFormat="DD/MM/YYYY"
    placeholder="dd/mm/yyyy"
    clearable={!locked}
    allowFreeInput={!locked}
    readOnly={locked}
    disabled={locked}
  />
);

const clientSignDateLabel = (
  <BilingualFieldLabel english="Sign Date (dd/mm/yyyy)" chinese="簽署日期" />
);

const OrderArFormPartE = ({ form, readOnly = false, disabled = false }) => {
  const arForm = getArFormValues(form);
  const locked = isWidgetReadOnly({ disabled, readOnly, form });
  const signatories = useMemo(
    () => resolvePartESignatories(form?.values?.orderId),
    [form?.values?.orderId]
  );
  const clientSignature = signatories.clientName1
    ? CLIENT_ONLINE_APPROVAL_SIGNATURE
    : "";
  const client2Signature = signatories.hasClient2
    ? CLIENT_ONLINE_APPROVAL_SIGNATURE
    : "";
  const entityName =
    arForm.entityName != null
      ? arForm.entityName
      : signatories.isCorporate
        ? signatories.entityName
        : "";
  const entitySignature =
    arForm.entitySignature != null
      ? arForm.entitySignature
      : signatories.isCorporate
        ? CLIENT_ONLINE_APPROVAL_SIGNATURE
        : "";

  return (
    <OrderArFormStack>
      <OrderArFormBlock
          title="Individual Investor / Joint Investors"
          subtitle="個人投資者 / 聯名投資者"
        >
          <Stack spacing="md">
            <Grid gutter="md" align="flex-start">
              <Grid.Col span={12} md={4}>
                <TextInput
                  styles={fieldStyles}
                  label={
                    <BilingualFieldLabel
                      english="Name of Client (I)"
                      chinese="客戶 (I) 姓名"
                    />
                  }
                  value={signatories.clientName1}
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <SignatureField
                  label={
                    <BilingualFieldLabel
                      english="Signature of Client (I)"
                      chinese="客戶 (I) 簽署"
                    />
                  }
                  value={clientSignature}
                />
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <SignDateField
                  label={clientSignDateLabel}
                  value={arForm.clientSignDate}
                  locked={locked}
                  onChange={(date) =>
                    setArFormField(form, "clientSignDate", date)
                  }
                />
              </Grid.Col>
            </Grid>
            <Grid gutter="md" align="flex-start">
              <Grid.Col span={12} md={4}>
                <TextInput
                  styles={fieldStyles}
                  label={
                    <BilingualFieldLabel
                      english="Name of Client (II)"
                      chinese="客戶 (II) 姓名"
                    />
                  }
                  value={signatories.clientName2}
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <SignatureField
                  label={
                    <BilingualFieldLabel
                      english="Signature of Client (II)"
                      chinese="客戶 (II) 簽署"
                    />
                  }
                  value={client2Signature}
                />
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <SignDateField
                  label={clientSignDateLabel}
                  value={signatories.hasClient2 ? arForm.clientSignDate2 : null}
                  locked={locked || !signatories.hasClient2}
                  onChange={(date) =>
                    setArFormField(form, "clientSignDate2", date)
                  }
                />
              </Grid.Col>
            </Grid>
            {!signatories.hasClient2 && (
              <Text size="xs" color="dimmed">
                Client (II) stays blank when the order has no second client.
              </Text>
            )}
          </Stack>
        </OrderArFormBlock>

      <OrderArFormBlock
        title="Corporate Investor"
        subtitle="公司投資者 — 須由授權的職員 / 合夥人簽署"
      >
        <Grid gutter="md" align="flex-start">
          <Grid.Col span={12} md={4}>
            <TextInput
              styles={fieldStyles}
              label={
                <BilingualFieldLabel
                  english="Name of Entity"
                  chinese="公司名稱"
                />
              }
              value={entityName}
              readOnly={locked}
              disabled={locked}
              onChange={(e) =>
                setArFormField(form, "entityName", e.currentTarget.value)
              }
            />
          </Grid.Col>
          <Grid.Col span={12} md={4}>
            <TextInput
              styles={fieldStyles}
              label={
                <BilingualFieldLabel
                  english="Signature of Authorized Officer"
                  chinese="授權職員簽署"
                />
              }
              value={entitySignature}
              readOnly={locked}
              disabled={locked}
              onChange={(e) =>
                setArFormField(form, "entitySignature", e.currentTarget.value)
              }
            />
          </Grid.Col>
          <Grid.Col span={12} md={4}>
            <SignDateField
              label={clientSignDateLabel}
              value={arForm.entitySignDate}
              locked={locked}
              onChange={(date) => setArFormField(form, "entitySignDate", date)}
            />
          </Grid.Col>
        </Grid>
      </OrderArFormBlock>
    </OrderArFormStack>
  );
};

export default OrderArFormPartE;
